import { z } from 'zod';
import type { ProductModule } from './api-modules.js';
import type { PlatformCapability, Role } from './enums.js';

// Route definition helpers shared by the per-domain route files.

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface RouteDef<
  P extends z.ZodType = z.ZodType,
  Q extends z.ZodType = z.ZodType,
  B extends z.ZodType = z.ZodType,
  R extends z.ZodType = z.ZodType,
> {
  method: HttpMethod;
  /** Express-style path relative to /api/v1, e.g. /workspaces/:workspaceId/projects */
  path: string;
  summary: string;
  tag: string;
  /** Minimum workspace role. Requires :workspaceId in the path. */
  minRole?: Role;
  /**
   * Product module the route belongs to (addendum §1). The AccessGuard refuses session-authenticated WRITE routes
   * (POST/PUT/PATCH/DELETE with minRole) with 409 `module_disabled` while the module is paused for the workspace.
   * Reads stay allowed (history, reports and settings remain visible to admins of a paused module); machine routes
   * (`public: 'ingress'`) keep their own gating in ingestion. `null` = explicitly shared by several modules.
   */
  module?: ProductModule | null;
  /** Platform capability required (global, not workspace-scoped). */
  capability?: PlatformCapability;
  /**
   * Requires a platform super operator: a user holding EVERY admin capability (isPlatformTeamAdmin). Used by
   * «Plataforma → Equipo Estateflow», where the platform capabilities themselves are managed.
   */
  platformTeamAdmin?: true;
  /**
   * public: no session required.
   *  - 'auth'    → auth flows (login, reset…), rate-limited
   *  - 'ingress' → authenticated by connection credentials (HMAC / bearer), never by cookie
   *  - 'system'  → health, OpenAPI
   */
  public?: 'auth' | 'ingress' | 'system';
  /** Session may be used before MFA verification (login step 2, enrollment, /me, logout). */
  allowUnverifiedMfa?: boolean;
  params?: P;
  query?: Q;
  body?: B;
  response: R;
  produces?: 'application/json' | 'text/event-stream' | 'application/octet-stream' | 'text/csv' | 'text/plain';
  /** Successful status code (default 200). */
  status?: 200 | 201 | 202;
}

export function route<P extends z.ZodType, Q extends z.ZodType, B extends z.ZodType, R extends z.ZodType>(
  def: RouteDef<P, Q, B, R>,
): RouteDef<P, Q, B, R> {
  return def;
}

/** Attribute every route of a domain file to a product module (routes that set `module` themselves keep it). */
export function inModule<T extends Record<string, RouteDef>>(module: ProductModule, defs: T): { [K in keyof T]: T[K] & { module: ProductModule | null } } {
  const out: Record<string, RouteDef> = {};
  for (const [name, def] of Object.entries(defs)) out[name] = def.module === undefined ? { ...def, module } : def;
  return out as { [K in keyof T]: T[K] & { module: ProductModule | null } };
}

/** Whether the module gate applies to a route: a session-authenticated workspace write attributed to a module. */
export function moduleGateOf(def: RouteDef): ProductModule | null {
  if (!def.module || def.public || !def.minRole || def.method === 'GET') return null;
  return def.module;
}

export const Ws = z.object({ workspaceId: z.string().min(1).max(64) });
export const W = <S extends z.ZodRawShape>(shape: S) => Ws.extend(shape);
export const IdParam = z.string().min(1).max(64);
export const items = <T extends z.ZodType>(t: T) => z.object({ items: z.array(t) });

